import MapsSkincareIcon from '@/components/icons/maps-skincare';
import { AboutUsStoreLocationElementDataType } from '@/components/sections/builder/elements/body/store-location/type';
import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

import { cn } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';

interface AboutUsAddressContent {
  heading: string;
  description: string;
}

interface AboutUsAddressData {
  name: string;
  contents: AboutUsAddressContent[];
  image?: string;
  buttonLabel?: string;
  buttonLink?: string;
  showButton?: boolean;
  showIcon?: boolean;
}

interface AboutUsAddressChild {
  id: string;
  type: string;
  group: string;
  data: AboutUsAddressData;
  parentId: string;
}

export default function ElementContentAddresses({ element }: ElementProps) {
  const content = element.data as AboutUsStoreLocationElementDataType;

  const childs = element.childs as AboutUsAddressChild[];

  return (
    <>
      <section
        className='color-scheme-address mx-auto w-full  duration-100'
        style={{
          paddingTop: content.padding?.top || 20,
          paddingBottom: content.padding?.bottom || 20,
          paddingLeft: content.padding?.left || 20,
          paddingRight: content.padding?.right || 20,
        }}
      >
        {content.style === 'grid' ? (
          <>
            <div className='flex flex-col px-4 lg:px-0'>
              <h1
                className='mb-6 text-2xl font-medium leading-9'
                dangerouslySetInnerHTML={{ __html: content.header as string }}
              />
              <div
                className={cn(
                  `grid gap-8 grid-cols-${content.limitMobile} md:grid-cols-${
                    content.limit
                  } lg:grid-cols-${content.limit || 3} lg:grid-cols-${content.limit || 3}`,
                  ((content.limit as number) <= 3 || !content.limit) && 'md:gap-10 lg:gap-20',
                  (content.limit as number) > 3 && 'lg:gap-10'
                )}
              >
                {(childs || []).map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: content,
                    }}
                    parentType={element.type}
                    key={child.id}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <h1
              className='text-2xl font-medium leading-9 '
              dangerouslySetInnerHTML={{ __html: content.header as string }}
            />
            <Carousel className='max-w-full'>
              <CarouselContent>
                {(childs || []).map((child) => (
                  <CarouselItem key={child.id}>
                    <div className='p-4'>
                      <div className='flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0'>
                        <div className='w-full md:w-1/2'>
                          <div className=' relative h-[350px] overflow-hidden rounded-lg md:h-[400px] '>
                            {child.data.image && (
                              <Image
                                src={
                                  child.data.image ||
                                  'https://store-assets.jubelio.com/webstore-v2/image…1161285_d20a6a73-c3a9-4be2-b04f-a09269030de9.jpeg'
                                }
                                alt={child.data.name || 'Store image'}
                                className='h-full w-full object-cover'
                                fill
                              />
                            )}
                          </div>
                        </div>
                        <div className='w-full md:w-1/2'>
                          <div className='flex flex-col gap-3'>
                            {child.data.contents?.map(
                              (item: AboutUsAddressContent, itemIdx: number) => (
                                <div key={itemIdx} className='mb-3'>
                                  <div
                                    className='text-secondary-store-location mb-1 text-base font-semibold leading-6 tracking-[-1.1%]'
                                    dangerouslySetInnerHTML={{ __html: item.heading || '' }}
                                  />
                                  <div
                                    className='rich-text-store-location text-sm leading-6 tracking-[-1.1%] text-gray-600'
                                    dangerouslySetInnerHTML={{ __html: item.description || '' }}
                                  />
                                </div>
                              )
                            )}
                            {child.data?.showButton && (
                              <div className=''>
                                <Link
                                  href={child.data?.buttonLink || '#'}
                                  className='inline-flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-base font-medium md:px-6 md:py-3 '
                                  style={{
                                    color: content.colorScheme?.btnSolidlabel || '#ffffff',
                                    backgroundColor:
                                      content.colorScheme?.btnSolidBackgroud || '#9b0062',
                                    borderColor:
                                      content.colorScheme?.btnOutline ||
                                      content.colorScheme?.btnSolidBackgroud ||
                                      '#9b0062',
                                  }}
                                >
                                  {child.data.showIcon && (
                                    <MapsSkincareIcon
                                      className='mr-2 h-5 w-5'
                                      color={content.colorScheme?.btnSolidlabel}
                                    />
                                  )}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: child.data.buttonLabel ?? '',
                                    }}
                                  ></span>
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className='left-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
              <CarouselNext className='right-4 h-12 w-12 [&>svg]:h-6 [&>svg]:w-6' />
            </Carousel>
          </>
        )}
      </section>
      <style jsx>
        {`
          .color-scheme-address {
            color: ${content.colorScheme?.text};
            background-color: ${content.colorScheme?.background};
          }
          .color-button-location {
            color: ${content.colorScheme?.btnSolidlabel};
            background-color: ${content.colorScheme?.btnSolidBackgroud};
          }
        `}
      </style>
    </>
  );
}
